<template>
  <div class="h-full grid grid-rows-[auto_1fr] overflow-hidden">
    <ThreadsNav :show-center="false" show-back>
      <template #center>
        <div>{{ store.activeNode.name }}</div>
      </template>
      <template #right>
        <div v-if="store.saveLoading" class="flex items-center">
          <span class="loading loading-spinner"></span>
          <span>{{ t("Message.saving") }}</span>
        </div>
        <button
          v-if="store.dialogList.length > 0"
          class="btn btn-primary btn-sm rounded"
          @click="onPreview(store.activeNode.id!, store.dialogList[0])"
        >
          {{ t("Buttons.preview") }}
        </button>
      </template>
    </ThreadsNav>

    <div ref="scrollDOM" class="overflow-auto pb-16" @scroll="onScroll">
      <div v-if="loading" class="text-center mt-32">
        <VanLoading color="#ABABAB" />
      </div>
      <div v-else-if="store.dialogList.length === 0" class="text-center mb-4">
        <VanEmpty :description="$t('Message.noDialoguesAvailable')" />

        <div class="flex flex-col items-center gap-2 mt-4">
          <button
            class="btn btn-primary btn-sm"
            @click="showStorySelectModal = true"
          >
            {{ t("game.fromStory") }}
          </button>

          <button class="btn btn-primary btn-sm" @click="showInputModal = true">
            {{ t("game.fromText") }}
          </button>
        </div>
      </div>
      <div
        v-for="(dialogId, i) in store.dialogList"
        v-else
        :key="dialogId"
        class="grid grid-cols-[auto_1fr] gap-3 mb-4 px-3"
      >
        <AddDialogBtnList :i="i" />
        <div class="flex flex-col items-center">
          <div
            v-if="getDialogById(dialogId).character.role === 4"
            class="border-2 border-[#3f3f3f] flex items-center justify-center w-[50px] aspect-[3/4] rounded"
          >
            <i class="iconfont icon-icon text-xl" />
          </div>

          <UserAvatar
            v-else
            :avatar-url="getCharacterByDialogId(dialogId).avatarMedia?.url"
            class="w-[50px] aspect-[3/4] rounded-sm"
          />
          <p class="my-0.5">#{{ i + 1 }}</p>
          <div
            class="flex items-center bg-neutral py-1 px-2 rounded"
            @click="clickShowSelectType(dialogId)"
          >
            <span class="mr-1">
              {{ t(`game.role.${getCharacterByDialogId(dialogId)?.role}`) }}
            </span>
            <i class="iconfont icon-down1 text-xs mt-[2px] inline-block" />
          </div>
        </div>
        <div class="flex-1 border rounded-md p-2.5 flex flex-col">
          <p class="flex-1" @click="clickEditText(getDialogById(dialogId))">
            {{ getDialogById(dialogId)?.text || t("Buttons.clickToFillIn") }}
            <i class="iconify mingcute--edit-3-line text-neutral-content" />
          </p>

          <!-- 管理 -->
          <div class="flex justify-between mt-4 font-light">
            <div class="flex items-center flex-wrap">
              <button
                :class="[
                  'btn btn-neutral btn-xs rounded border-none mr-2 px-1',
                  (getDialogById(dialogId).sceneMediaId ||
                    getDialogById(dialogId).portraitzz?.length > 0) &&
                    'text-primary',
                ]"
                @click="clickEditImg(dialogId)"
              >
                <i class="iconify mingcute--pic-line1 text-xs font-light" />
              </button>
              <button
                :class="[
                  'btn btn-neutral btn-xs rounded border-none mr-2 px-1',
                  getDialogById(dialogId).isAudioEnabled && 'text-primary',
                ]"
                @click="clickEditAudio(dialogId)"
              >
                <i class="iconfont icon-volume text-xs font-light" />
              </button>
              <button
                class="btn btn-neutral btn-xs rounded border-none mr-2 px-1"
                @click="onPreview(store.activeNode.id!, dialogId)"
              >
                <i class="iconfont icon-preview text-xs mr-[-3px] font-light" />
                <span>{{ t("Buttons.preview") }}</span>
              </button>
            </div>
            <div class="flex items-center flex-wrap">
              <button
                class="btn btn-neutral btn-xs rounded border-none mr-2 px-1"
                @click="clickConnection('dialog', dialogId)"
              >
                <i
                  :class="[
                    'iconfont icon-connection text-xs',
                    getDialogById(dialogId).nextDialog
                      ? 'text-primary'
                      : 'text-neutral-content',
                  ]"
                />
              </button>
              <button
                v-if="i !== 0"
                class="btn btn-neutral btn-xs rounded border-none mr-2 px-1"
                @click="clickSort(dialogId, true)"
              >
                <i class="iconfont icon-up text-neutral-content text-xs" />
              </button>
              <button
                v-if="i !== store.dialogList.length - 1"
                class="btn btn-neutral btn-xs rounded border-none mr-2 px-1"
                @click="clickSort(dialogId, false)"
              >
                <i class="iconfont icon-down text-neutral-content text-xs" />
              </button>
              <button
                class="btn btn-neutral btn-xs rounded border-none mr-2 px-1"
                @click="store.deleteDialog(getDialogById(dialogId))"
              >
                <i class="iconfont icon-delete text-neutral-content text-xs" />
              </button>
            </div>
          </div>
        </div>

        <!-- 玩家回复选项 -->
        <div class="col-span-2 flex justify-between">
          <div class="flex items-center">
            <span>{{ t("game.playerResponse") }}：</span>
            <template v-if="hasConnect(dialogId)">
              <div>
                {{ t("game.connected") }}（{{ getConnectInfo(dialogId) }}）
              </div>

              <div
                class="p-1"
                @click="
                  getDialogById(dialogId).isOpen =
                    !getDialogById(dialogId).isOpen
                "
              >
                <i
                  v-if="!getDialogById(dialogId).isOpen"
                  class="iconfont icon-left1 text-xs"
                />
                <i v-else class="iconfont icon-down1" />
              </div>
            </template>
          </div>
          <div>
            <!-- <button
              v-if="hasConnect(dialogId)"
              class="btn btn-neutral btn-xs rounded mr-2 px-4"
              @click="clickJump(getDialogById(dialogId))"
            >
              {{ t("Buttons.jumpToSettings") }}
            </button> -->
            <button
              v-if="getDialogById(dialogId).optionzz.length < 6"
              class="btn btn-neutral btn-xs rounded mr-2 px-4"
              @click="store.addDialogOption(getDialogById(dialogId).id!)"
            >
              + {{ t("Buttons.addReplyOption") }}
            </button>
          </div>
        </div>

        <ul v-if="getDialogById(dialogId).isOpen" class="col-span-2">
          <li
            v-for="option in getDialogById(dialogId).optionzz"
            :key="option.id"
            class="flex px-3 mb-2.5"
          >
            <div class="bg-neutral rounded px-2.5 py-2 flex-1">
              <div>
                <div
                  class="underline flex-1"
                  @click="clickEditDialogOptionText(option)"
                >
                  {{ option?.text || t("Buttons.clickToFillIn") }}
                  <i
                    class="iconify mingcute--edit-3-line text-neutral-content"
                  />
                </div>
                <div
                  class="gap-1 py-1 text-center rounded cursor-pointer flex flex-wrap my-3"
                  @click="clickEditDialogOptionScore(option)"
                >
                  <button
                    v-for="text in scoreListFromScoreText(option.score)"
                    v-if="scoreListFromScoreText(option.score).length > 0"
                    class="btn btn-primary btn-sm ml-2.5 text-neutral-content"
                  >
                    {{ text }}
                  </button>
                  <button
                    v-else
                    class="btn btn-primary btn-sm ml-2.5 text-neutral-content"
                  >
                    {{ t(`game.values.title`) }}
                  </button>
                </div>
              </div>
              <div
                v-if="option.nextDialog"
                class="bg-primary ml-[80px] px-2.5 py-1 mt-2.5"
              >
                {{ store.filterOptionText(option.nextDialog) }}
                {{ store.filterNextDialogText(option.nextDialog) }}
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <button
                :class="[
                  'btn btn-primary btn-sm ml-2.5 ',
                  option.nextDialog ? 'text-primary' : 'text-neutral-content',
                ]"
                @click="clickConnection('option', option.id!)"
              >
                <i class="iconfont icon-connection" />
              </button>
              <button
                class="btn btn-primary btn-sm ml-2.5 text-neutral-content"
                @click="store.deleteDialogOption(option)"
              >
                <i class="iconfont icon-delete" />
              </button>
            </div>
          </li>
        </ul>
      </div>
      <AddDialogBtnList v-if="!loading" />

      <div
        class="fixed bottom-0 left-0 right-0 bg-primary py-5 flex justify-center"
      >
        <button
          class="btn btn-xs rounded mr-3 fon"
          @click="clickToggleOpenOption"
        >
          {{ isAllOpen ? $t("Buttons.expand") : $t("Buttons.collapse") }}
          {{ $t("Buttons.allReplies") }}
        </button>
      </div>
    </div>
  </div>

  <ModalPublishGalGame />
  <ModalJump />
  <ModalJumpList />
  <ModalInput />
  <ModalScoreSetting />

  <DialogTextInput v-model:show="showDialogEdit" />

  <ToneSelectionPopup
    v-model:show="showToneModal"
    :selected-tone-id="selectedToneId"
    @update:selected-tone-id="onSelectTone"
  />
  <GalGamePreviewPopup
    :id="store.galGameId"
    ref="previewPopupRef"
    :uuid="store.galGameUUID"
  />

  <van-action-sheet
    v-model:show="show"
    :actions="actions"
    close-on-click-action
    @select="onSelect"
  />

  <ModalSelectCharacterGalGameEditor
    v-model:show="showSelectType"
    @confirm="onConfirm"
  />
  <ModalStorySelect
    v-model:loading="storySelectLoading"
    v-model:select-chapter-id="activeStoryId"
    v-model:show="showStorySelectModal"
    @confirm="confirmStoryInsert"
  />
  <BaseInputModal
    v-model:show="showInputModal"
    :title="t('game.fromText')"
    type="textarea"
    @confirm="confirmInput"
  />
</template>

<script lang="ts" setup>
import { useGalGameEditor } from "@/stores/galGameEditor";
import { StorageSet } from "@/tools/localStorage";
import { showLoadingToast } from "vant";
import { ref, onMounted, nextTick, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { GalGamePreviewPopupInstance } from "@/components/mobile/gal-game/editor/GalGamePreviewPopup.vue";
import { useI18n } from "vue-i18n";
import DialogTextInput from "@/components/mobile/gal-game/editor/components/DialogTextInput.vue";
import { sessionStorageSet } from "@/tools/sessionStorage";

const { t } = useI18n();
const store = useGalGameEditor();
const router = useRouter();
const loading = ref(true);
const route = useRoute();
const show = ref(false);
const actions = ref<any[]>([]);

const showToneModal = ref(false);
const selectedToneId = ref(0);
const previewPopupRef = ref<null | GalGamePreviewPopupInstance>(null);

const activeDialogId = ref(0);
const showSelectType = ref(false);
const activeType = ref<number[]>([]);
const showDialogEdit = ref(false);

const clickShowSelectType = (dialogId: number) => {
  activeDialogId.value = dialogId;
  activeType.value = [getCharacterByDialogId(dialogId).id!];
  showSelectType.value = true;
};

const onConfirm = (characterId: number) => {
  const dialog = getDialogById(activeDialogId.value);
  if (characterId) {
    dialog.characterId = characterId;
    dialog.character.role = store.characterIdMap[characterId].role;
    const { mateToneId } = store.getDefaultDialog(characterId);
    dialog.mateToneId = mateToneId;
    StorageSet(
      `galGameRoleSaveCache-${store.galGameId}-${dialog.character.role}`,
      characterId
    );
    store.saveDialog(dialog);
  }
  showSelectType.value = false;
};

const getDialogById = (dialogId: number) => store.dialogMap[dialogId];

const getCharacterByDialogId = (dialogId: number) =>
  store.characterIdMap[getDialogById(dialogId).characterId!];

const hasConnect = (dialogId: number) =>
  getDialogById(dialogId).optionzz.length > 0;

const getConnectInfo = (dialogId: number) => {
  const { optionzz } = getDialogById(dialogId);
  const totalCount = optionzz.length;
  const hasNextCount = optionzz.filter((item) => item.nextDialog).length;
  return `${hasNextCount}/${totalCount}`;
};

const clickEditText = (item: GalGameDialog) => {
  store.modalInputContent = item?.text || "";
  store.activeDialog = item;
  showDialogEdit.value = true;
};

const clickEditDialogOptionText = (item: GalGameDialogOption) => {
  store.showInputModal = true;
  store.modalInputContent = item?.text;
  store.modalInputType = "dialogOption";
  store.activeDialog = store.dialogMap[item.dialogId];
  store.activeDialogOption = item;
};

const valueIdNameMap = computed(() => {
  const map: Record<number, string> = {};
  for (const score of store.scoreList) map[score.id!] = score.name;
  return map;
});
const scoreListFromScoreText = (score: string) => {
  if (!score) return [];
  const scoreArr = score.split("&&");
  const scoreList: string[] = [];
  for (const item of scoreArr) {
    const [key, value] = item.split("=");
    const [_, cid, id] = key.split("_");
    // if (ids.length !== 3) continue;
    if (!id) continue;
    let text = ``;
    if (Number(cid)) text += `${store.characterIdMap[Number(cid)].name}.`;
    text += valueIdNameMap.value[Number(id)];
    if (Number(value) > 0) {
      text += `+${value}`;
    } else {
      text += value;
    }
    scoreList.push(text);
  }
  return scoreList;
};

const clickEditDialogOptionScore = (item: GalGameDialogOption) => {
  store.activeDialogOption = item;
  store.showScoreSettingModal = true;
};

const clickJump = (dialog: GalGameDialog) => {
  store.activeDialog = dialog;
  store.showJumpSettingModal = true;
};

const clickSort = (id: number, isUp: boolean) => {
  const idx = store.dialogList.findIndex((dialogId) => dialogId === id);
  if (isUp) {
    const prev = store.dialogList[idx - 1];
    store.dialogList[idx - 1] = id;
    store.dialogList[idx] = prev;
  } else {
    const next = store.dialogList[idx + 1];
    store.dialogList[idx + 1] = id;
    store.dialogList[idx] = next;
  }
  store.sortDialog();
};

const clickEditImg = (dialogId: number) => {
  store.activeDialog = store.dialogMap[dialogId];
  StorageSet("activeDialog", store.activeDialog);
  if (store.activeDialog.sceneMediaId) {
    actions.value = [
      { name: t("game.actions.editImage"), action: "editImg" },
      { name: t("game.actions.removeImage"), action: "removeImg" },
    ];
    show.value = true;
  } else {
    router.push({
      name: "performanceEditor",
      query: {
        dialogId,
        nodeId: store.activeDialog.storyNodeId,
        galGameId: store.galGameId,
      },
    });
  }
};

const clickEditAudio = (dialogId: number) => {
  store.activeDialog = store.dialogMap[dialogId];
  store.activeDialog.isAudioEnabled = !store.activeDialog.isAudioEnabled;
  store.saveDialog();
};

const clickConnection = (type: string, id: number) => {
  if (type === "dialog") {
    store.activeDialog = store.dialogMap[id];
    store.activeCheckedJumpDialogId = store.activeDialog.nextDialogId!;
  } else if (type === "option") {
    store.activeDialogOption = store.optionMap[id];
    store.activeDialog = store.dialogMap[store.activeDialogOption.dialogId];
    store.activeCheckedJumpDialogId = store.activeDialogOption.nextDialogId!;
  }
  store.connectionType = type;
  store.showJumpListModal = true;
};

const onSelectTone = async (toneId: number) => {
  selectedToneId.value = toneId;
  store.activeDialog.mateToneId = toneId;
  showToneModal.value = false;
  store.saveDialog();
};

const onPreview = async (nodeId: number, dialogId?: number) => {
  const loading = showLoadingToast({ message: t("Message.loading") });
  try {
    await previewPopupRef.value?.startPreview({ dialogId, nodeId: nodeId });
  } finally {
    loading.close();
  }
};

const onSelect = (item: any) => {
  const { action } = item;
  if (action === "editImg") {
    router.push({
      name: "performanceEditor",
      query: {
        galGameId: store.galGameId,
        dialogId: store.activeDialog.id,
        nodeId: store.activeDialog.storyNodeId,
      },
    });
  } else if (action === "removeImg") {
    store.activeDialog.sceneMediaId = null;
    store.saveDialog();
  } else if (action === "editAudio") {
    showToneModal.value = true;
  } else if (action === "removeAudio") {
    store.activeDialog.mateToneId = null;
    store.saveDialog();
  }
};

let isAllOpen = ref(true);
const clickToggleOpenOption = () => {
  store.dialogList.forEach((dialogId) => {
    const dialog = store.dialogMap[dialogId];
    dialog.isOpen = isAllOpen.value;
  });
  isAllOpen.value = !isAllOpen.value;
};

const scrollDOM = ref<HTMLDivElement | null>(null);

const onScroll = (e: Event) => {
  const target = e.target as HTMLDivElement;
  store.scrollTopDialogCache = target.scrollTop;
};

// story 导入
const showStorySelectModal = ref(false);
const storySelectLoading = ref(false);
const activeStoryId = ref(0);
const confirmStoryInsert = async (story: Story) => {
  router.push({
    name: "storyImgModule",
    query: {
      story: story.uuid,
      storyNodeId: store.activeNode.id,
      characterId: store.form.narrator.id,
    },
  });
};

// text 导入
const showInputModal = ref(false);
const confirmInput = (text: string) => {
  let textList: string[] = [];
  text.split("\n").forEach((line) => {
    line = line.trim();
    line && textList.push(line);
  });
  sessionStorageSet("text2GameList", textList);
  router.push({
    name: "storyImgModule",
    query: {
      storyNodeId: store.activeNode.id,
      characterId: store.form.narrator.id,
    },
  });
  showInputModal.value = false;
};

onMounted(async () => {
  loading.value = true;
  const uuid = route.params.uuid as string;
  const { nodeId } = route.query;
  if (store.activeNode.id !== Number(nodeId)) store.dialogList = [];
  const _nodeId = Number(nodeId);
  await store.initGalGame(uuid);
  await store.initNode(store.galGameId);
  store.activeNode = store.nodeMap[_nodeId];
  await store.initDialog(_nodeId);
  loading.value = false;
  nextTick(() => {
    if (scrollDOM.value) {
      scrollDOM.value.scrollTop = store.scrollTopDialogCache;
    }
  });
});
</script>
